import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, CheckCircle, AlertTriangle, Clock, Users, FileText, Settings, Zap } from 'lucide-react';

interface DemoTask {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  owner: string;
  dueDate: string;
  category: 'setup' | 'data' | 'config' | 'training' | 'golive';
  isClientTask: boolean;
}

interface DemoWorkspaceViewProps {
  projectId?: string;
}

export const DemoWorkspaceView: React.FC<DemoWorkspaceViewProps> = ({ projectId = 'demo-001' }) => {
  const [demoTasks, setDemoTasks] = useState<DemoTask[]>([
    {
      id: 'demo-1',
      title: 'Complete Tyler Tech Account Setup',
      description: 'Verify account credentials and initial system access',
      status: 'completed',
      owner: 'Client',
      dueDate: '2026-01-15',
      category: 'setup',
      isClientTask: true
    },
    {
      id: 'demo-2',
      title: 'Upload County Data Files',
      description: 'Provide property records and tax assessment data',
      status: 'in_progress',
      owner: 'Client',
      dueDate: '2026-01-18',
      category: 'data',
      isClientTask: true
    },
    {
      id: 'demo-3',
      title: 'Configure User Permissions',
      description: 'Set up role-based access for county staff',
      status: 'pending',
      owner: 'Tyler PM',
      dueDate: '2026-01-20',
      category: 'config',
      isClientTask: false
    },
    {
      id: 'demo-4',
      title: 'Schedule Staff Training Sessions',
      description: 'Coordinate training for 25 county employees',
      status: 'pending',
      owner: 'Client',
      dueDate: '2026-01-25',
      category: 'training',
      isClientTask: true
    },
    {
      id: 'demo-5',
      title: 'Data Migration Validation',
      description: 'Verify all county records migrated correctly',
      status: 'blocked',
      owner: 'Tyler Engineer',
      dueDate: '2026-01-22',
      category: 'data',
      isClientTask: false
    }
  ]);

  const [showSupportDemo, setShowSupportDemo] = useState(false);

  const handleTaskComplete = (taskId: string) => {
    setDemoTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, status: 'completed' as const }
        : task
    ));
  };

  const handleBlockerToggle = (taskId: string) => {
    const task = demoTasks.find(t => t.id === taskId);
    if (task?.status === 'blocked') {
      setShowSupportDemo(true);
    } else {
      setDemoTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, status: 'blocked' as const }
          : t
      ));
    }
  };

  const resetDemo = () => {
    setDemoTasks(prev => prev.map(task => ({
      ...task,
      status: task.id === 'demo-1' ? 'completed' : 
              task.id === 'demo-2' ? 'in_progress' :
              task.id === 'demo-5' ? 'blocked' : 'pending'
    })));
    setShowSupportDemo(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-[#8A9150]" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-[#4267A1]" />;
      case 'blocked': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default: return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'setup': return <Settings className="w-5 h-5" />;
      case 'data': return <FileText className="w-5 h-5" />;
      case 'config': return <Zap className="w-5 h-5" />;
      case 'training': return <Users className="w-5 h-5" />;
      default: return <Play className="w-5 h-5" />;
    }
  };

  const completedTasks = demoTasks.filter(t => t.status === 'completed').length;
  const progressPercent = Math.round((completedTasks / demoTasks.length) * 100);

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent">
              Interactive Demo Workspace
            </h2>
            <button
              onClick={resetDemo}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Reset Demo
            </button>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-yellow-800 font-medium">Demo Mode Active</span>
            </div>
            <p className="text-yellow-700 text-sm mt-2">
              This is a pre-seeded Tyler Technologies onboarding workflow. Try completing tasks and triggering support escalations.
            </p>
          </div>

          <div className="p-6 mb-8 rounded-xl bg-white border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Franklin County - Property Management System</h3>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#4267A1]">{progressPercent}%</div>
                <div className="text-sm text-gray-600">Complete</div>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] h-3 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{completedTasks} of {demoTasks.length} tasks completed</span>
              <span>Target Go-Live: February 1, 2024</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {demoTasks.map((task) => (
            <motion.div
              key={task.id}
              whileHover={{ scale: 1.01 }}
              className="tyler-card p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleTaskComplete(task.id)}
                    disabled={task.status === 'completed' || !task.isClientTask}
                    className="flex-shrink-0"
                  >
                    {getStatusIcon(task.status)}
                  </button>
                  
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getCategoryIcon(task.category)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{task.title}</h4>
                      <p className="text-sm text-gray-600">{task.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Owner: {task.owner}</span>
                        <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                        <span className={`px-2 py-1 rounded-full ${
                          task.isClientTask ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.isClientTask ? 'Client Task' : 'Tyler Task'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {task.status === 'blocked' && (
                    <button
                      onClick={() => setShowSupportDemo(true)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      View Support Ticket
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleBlockerToggle(task.id)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      task.status === 'blocked'
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                  >
                    {task.status === 'blocked' ? 'Unblock' : 'Set Blocker'}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {showSupportDemo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Support Bridge Demo</h3>
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-red-900">Ticket #SB-2024-001</span>
                  </div>
                  <p className="text-red-800 text-sm">
                    Data Migration Validation Failed - County records showing inconsistencies in property assessments.
                  </p>
                  <div className="mt-3 text-xs text-red-600">
                    <div>Status: Open</div>
                    <div>SLA: 4 hours remaining</div>
                    <div>Assigned: Tyler Support Team</div>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm">
                  This demonstrates how blocked tasks automatically create Support Bridge tickets with full context.
                </p>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowSupportDemo(false)}
                    className="flex-1 px-4 py-2 bg-[#4267A1] text-white rounded-lg font-medium hover:bg-[#3A5A91] transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
